import * as actionTypes from "../actions/actionTypes";
import { updateObject } from "../../components/Utils/Custom/HelperCode";
import { axiosInstance } from "../../components/Utils/API/axiosConfig";
import { AuthReducerModel } from "../../components/Utils/Models/store";
import { hasSomeAuthorization, isActionsAllowed } from "../../components/Auth/actionsCheck";
import { EScopesType } from "../../components/Utils/Models/auth";
import { InternalAccountAssignedModel } from "../../components/Utils/Models/account";

const initialState: AuthReducerModel = {
  uid: "",
  token: "",
  scopes: [],
  userName: "",
  accountName: "",
  email: "",
  isAdmin: false,
  role: "",
  accountId: 0,
  isEmailVerified: true,
  accountStatus: "",

  isSigningIn: false,
  isAuthenticated: false,
  signInErrorCode: "",

  isSigningOut: false,
  signOutErrorCode: "",

  isVerifying: false,

  isReauthenticateCredentialRequesting: false,
  isReautenticateCredentialSuccess: false,
  reautenticateCredentialErrorCode: "",

  isUpdatePasswordRequesting: false,
  isUpdatePasswordSuccess: false,
  updatePasswordErrorCode: "",

  isSignUpRequesting: false,
  isSignUpSuccess: false,
  signUpErrorCode: "",

  isVerifyEmailRequesting: false,
  isVerifyEmailSuccess: false,
  verifyEmailErrorCode: "",

  isVerifyPasswordResetCodeRequesting: false,
  isVerifyPasswordResetCodeSuccess: false,
  verifyPasswordResetCodeErrorCode: "",
  emailPasswordToReset: "",

  isConfirmPasswordResetRequesting: false,
  isConfirmPasswordResetSuccess: false,
  confirmPasswordResetErrorCode: "",

  isSendVerificationEmailRequesting: false,
  isSendVerificationEmailSuccess: false,
  sendVerificationEmailErrorCode: "",

  //authorizations
  hasPermissionSupport: false,
  hasPermissionSupportAdmin: false,
  hasPermissionShippingRead: false,
  hasPermissionShippingWrite: false,
  hasPermissionSupportOperatorRead: false,
  hasPermissionSupportOperatorWrite: false,
  hasPermissionPortalAccessRead: false,
  accountAssigned: {
    id: 0,
    name: "",
  } as InternalAccountAssignedModel,
};

const updateAuthorization = (scopes: string[]) => {
  const hasPermissionSupport = isActionsAllowed(scopes, [EScopesType.support_r]);
  const hasPermissionSupportAdmin = isActionsAllowed(scopes, [EScopesType.support_w]);
  const hasPermissionShippingRead = isActionsAllowed(scopes, [EScopesType.shipping_r]);
  const hasPermissionShippingWrite = isActionsAllowed(scopes, [EScopesType.shipping_w]);
  const hasPermissionSupportOperatorRead = isActionsAllowed(scopes, [EScopesType.operator_r]);
  const hasPermissionSupportOperatorWrite = isActionsAllowed(scopes, [EScopesType.operator_w]);
  const hasPermissionPortalAccessRead = isActionsAllowed(scopes, [
    EScopesType.supportportalaccess_r,
  ]);

  return {
    hasPermissionSupport,
    hasPermissionSupportAdmin,
    hasPermissionShippingRead,
    hasPermissionShippingWrite,
    hasPermissionSupportOperatorRead,
    hasPermissionSupportOperatorWrite,
    hasPermissionPortalAccessRead,
  };
};
//////////////Reducer Slice//////////////////////
const updateToken_Reducer = (state: AuthReducerModel, action: any) => {
  const refreshToken = action.refreshToken;
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${refreshToken.token}`;

  return updateObject(state, {
    isSigningIn: false,
    isAuthenticated: true,
    token: refreshToken.token,
    userName: refreshToken.claims.userName,
    email: refreshToken.claims.email,
    accountName: refreshToken.claims.accountName,
    scopes: refreshToken.claims.scopes,
    isAdmin: refreshToken.claims.isAdmin,
    role: refreshToken.claims.role,
    isEmailVerified: refreshToken.claims.email_verified,
    accountId: refreshToken.claims.accountId,
    accountStatus: refreshToken.claims.accountStatus,
    uid: refreshToken.claims.activeUserId,
    ...updateAuthorization(refreshToken.claims.scopes),
  } as AuthReducerModel);
};

const signInRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, { isSigningIn: true, signInErrorCode: "" } as AuthReducerModel);
};

const signInSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  const decodedToken = action.decodedToken;
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${decodedToken.token}`;
  return updateObject(state, {
    isSigningIn: false,
    isAuthenticated: true,
    token: decodedToken.token,
    userName: decodedToken.claims.userName,
    email: decodedToken.claims.email,
    accountName: decodedToken.claims.accountName,
    scopes: decodedToken.claims.scopes,
    isAdmin: decodedToken.claims.isAdmin,
    role: decodedToken.claims.role,
    isEmailVerified: decodedToken.claims.email_verified,
    accountId: decodedToken.claims.accountId,
    accountStatus: decodedToken.claims.accountStatus,
    uid: decodedToken.claims.activeUserId,
    ...updateAuthorization(decodedToken.claims.scopes),
  } as AuthReducerModel);
};

const signInFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSigningIn: false,
    isAuthenticated: false,
    signInErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const signOutRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, { isSigningOut: true } as AuthReducerModel);
};

const signOutSuccess_Reducer = async (state: AuthReducerModel, action: any) => {
  axiosInstance.defaults.headers.common.Authorization = "";
  return updateObject(state, { ...initialState } as AuthReducerModel);
};

const signOutFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSigningOut: false,
    signOutErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const verifyRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, { isVerifying: true } as AuthReducerModel);
};

const verifySuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, { isVerifying: false } as AuthReducerModel);
};

const reauthenticateCredentialRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isReauthenticateCredentialRequesting: true,
    isReautenticateCredentialSuccess: false,
    reautenticateCredentialErrorCode: "",
  } as AuthReducerModel);
};

const reauthenticateCredentialSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isReauthenticateCredentialRequesting: false,
    isReautenticateCredentialSuccess: true,
  } as AuthReducerModel);
};

const reauthenticateCredentialFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isReauthenticateCredentialRequesting: false,
    isReautenticateCredentialSuccess: false,
    reautenticateCredentialErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const updatePasswordRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isUpdatePasswordRequesting: true,
    isUpdatePasswordSuccess: false,
    updatePasswordErrorCode: "",
  } as AuthReducerModel);
};

const updatePasswordSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isUpdatePasswordRequesting: false,
    isUpdatePasswordSuccess: true,
  } as AuthReducerModel);
};

const updatePasswordFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isUpdatePasswordRequesting: false,
    isUpdatePasswordSuccess: false,
    updatePasswordErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const signUpRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSignUpRequesting: true,
    isSignUpSuccess: false,
    signUpErrorCode: "",
  } as AuthReducerModel);
};

const signUpSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSignUpRequesting: false,
    isSignUpSuccess: true,
  } as AuthReducerModel);
};

const signUpFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSignUpRequesting: false,
    isSignUpSuccess: false,
    signUpErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const verifyEmailRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyEmailRequesting: true,
    isVerifyEmailSuccess: false,
    verifyEmailErrorCode: "",
  } as AuthReducerModel);
};

const verifyEmailSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyEmailRequesting: false,
    isVerifyEmailSuccess: true,
  } as AuthReducerModel);
};

const verifyEmailFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyEmailRequesting: false,
    isVerifyEmailSuccess: false,
    verifyEmailErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const verifyPasswordResetCodeRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyPasswordResetCodeRequesting: true,
    isVerifyPasswordResetCodeSuccess: false,
    verifyPasswordResetCodeErrorCode: "",
    emailPasswordToReset: "",
  } as AuthReducerModel);
};

const verifyPasswordResetCodeSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyPasswordResetCodeRequesting: false,
    isVerifyPasswordResetCodeSuccess: true,
    emailPasswordToReset: action.email,
  } as AuthReducerModel);
};

const verifyPasswordResetCodeFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isVerifyPasswordResetCodeRequesting: false,
    isVerifyPasswordResetCodeSuccess: false,
    verifyPasswordResetCodeErrorCode: action.errorCode,
    emailPasswordToReset: "",
  } as AuthReducerModel);
};

const confirmPasswordResetRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isConfirmPasswordResetRequesting: true,
    isConfirmPasswordResetSuccess: false,
    confirmPasswordResetErrorCode: "",
  } as AuthReducerModel);
};

const confirmPasswordResetSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isConfirmPasswordResetRequesting: false,
    isConfirmPasswordResetSuccess: true,
  } as AuthReducerModel);
};

const confirmPasswordResetFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isConfirmPasswordResetRequesting: false,
    isConfirmPasswordResetSuccess: false,
    confirmPasswordResetErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const sendVerificationEmailRequest_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSendVerificationEmailRequesting: true,
    isSendVerificationEmailSuccess: false,
    sendVerificationEmailErrorCode: "",
  } as AuthReducerModel);
};

const sendVerificationEmailSuccess_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSendVerificationEmailRequesting: false,
    isSendVerificationEmailSuccess: true,
  } as AuthReducerModel);
};

const sendVerificationEmailFailure_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    isSendVerificationEmailRequesting: false,
    isSendVerificationEmailSuccess: false,
    sendVerificationEmailErrorCode: action.errorCode,
  } as AuthReducerModel);
};

const updateAccountAssigned_Reducer = (state: AuthReducerModel, action: any) => {
  return updateObject(state, {
    accountAssigned: { ...action.accountAssigned },
  } as AuthReducerModel);
};

/////REDUCER//////
const reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case actionTypes.SIGNIN_REQUEST:
      return signInRequest_Reducer(state, action);
    case actionTypes.SIGNIN_SUCCESS:
      return signInSuccess_Reducer(state, action);
    case actionTypes.SIGNIN_FAILURE:
      return signInFailure_Reducer(state, action);
    case actionTypes.SIGNOUT_REQUEST:
      return signOutRequest_Reducer(state, action);
    case actionTypes.SIGNOUT_SUCCESS:
      return signOutSuccess_Reducer(state, action);
    case actionTypes.SIGNOUT_FAILURE:
      return signOutFailure_Reducer(state, action);
    case actionTypes.VERIFY_REQUEST:
      return verifyRequest_Reducer(state, action);
    case actionTypes.VERIFY_SUCCESS:
      return verifySuccess_Reducer(state, action);

    case actionTypes.REAUTHENTICATE_CREDENTIAL_REQUEST:
      return reauthenticateCredentialRequest_Reducer(state, action);
    case actionTypes.REAUTHENTICATE_CREDENTIAL_SUCCESS:
      return reauthenticateCredentialSuccess_Reducer(state, action);
    case actionTypes.REAUTHENTICATE_CREDENTIAL_FAILURE:
      return reauthenticateCredentialFailure_Reducer(state, action);

    case actionTypes.UPDATE_PASSWORD_REQUEST:
      return updatePasswordRequest_Reducer(state, action);
    case actionTypes.UPDATE_PASSWORD_SUCCESS:
      return updatePasswordSuccess_Reducer(state, action);
    case actionTypes.UPDATE_PASSWORD_FAILURE:
      return updatePasswordFailure_Reducer(state, action);

    case actionTypes.SIGNUP_REQUEST:
      return signUpRequest_Reducer(state, action);
    case actionTypes.SIGNUP_SUCCESS:
      return signUpSuccess_Reducer(state, action);
    case actionTypes.SIGNUP_FAILURE:
      return signUpFailure_Reducer(state, action);

    case actionTypes.VERIFY_EMAIL_REQUEST:
      return verifyEmailRequest_Reducer(state, action);
    case actionTypes.VERIFY_EMAIL_SUCCESS:
      return verifyEmailSuccess_Reducer(state, action);
    case actionTypes.VERIFY_EMAIL_FAILURE:
      return verifyEmailFailure_Reducer(state, action);

    case actionTypes.VERIFY_PASSWORD_RESETCODE_REQUEST:
      return verifyPasswordResetCodeRequest_Reducer(state, action);
    case actionTypes.VERIFY_PASSWORD_RESETCODE_SUCCESS:
      return verifyPasswordResetCodeSuccess_Reducer(state, action);
    case actionTypes.VERIFY_PASSWORD_RESETCODE_FAILURE:
      return verifyPasswordResetCodeFailure_Reducer(state, action);

    case actionTypes.CONFIRM_PASSWORD_RESET_REQUEST:
      return confirmPasswordResetRequest_Reducer(state, action);
    case actionTypes.CONFIRM_PASSWORD_RESET_SUCCESS:
      return confirmPasswordResetSuccess_Reducer(state, action);
    case actionTypes.CONFIRM_PASSWORD_RESET_FAILURE:
      return confirmPasswordResetFailure_Reducer(state, action);

    case actionTypes.SEND_VERIFICATION_EMAIL_REQUEST:
      return sendVerificationEmailRequest_Reducer(state, action);
    case actionTypes.SEND_VERIFICATION_EMAIL_SUCCESS:
      return sendVerificationEmailSuccess_Reducer(state, action);
    case actionTypes.SEND_VERIFICATION_EMAIL_FAILURE:
      return sendVerificationEmailFailure_Reducer(state, action);

    case actionTypes.UPDATE_TOKEN:
      return updateToken_Reducer(state, action);

    case actionTypes.UPDATE_ACCOUNT_ASSIGNED:
      return updateAccountAssigned_Reducer(state, action);

    default:
      return state;
  }
};
export default reducer;
